import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useTheme } from '../context/ThemeContext';
import { FaUser, FaUserShield, FaUsers, FaLifeRing, FaMoon, FaSun, FaSignOutAlt, FaHome } from 'react-icons/fa';
import AuthService from '../services/AuthService';
import { getLevelTitle } from '../utils/levelUtils';
import toast from 'react-hot-toast';

// Shared top-bar profile menu (avatar + dropdown with theme toggle) so every
// page has the same account controls. Styles live in Dashboard.css (.menu-*,
// .level-chip, .top-bar-right) which is bundled app-wide.
const ProfileMenu = ({ showLevel = false }) => {
  const navigate = useNavigate();
  const { state, actions } = useUser();
  const { theme, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);

  const user = state.user;
  const level = state.progress?.level ?? 1;

  const isAdmin =
    user?.isAdmin || user?.adminLevel === 'admin' || user?.adminLevel === 'master_admin';
  const isTeacherOrAdmin =
    user?.isAdmin ||
    ['1', '2', '3', 'master_admin'].includes(user?.adminLevel) ||
    user?.isTeacher;

  const go = (to) => {
    setMenuOpen(false);
    navigate(to);
  };

  const handleLogout = () => {
    setMenuOpen(false);
    if (!window.confirm('Are you sure you want to log out?')) return;
    AuthService.logout();
    actions.setUser(null);
    actions.setOnboarded(false);
    localStorage.clear();
    sessionStorage.clear();
    toast.success('Logged out successfully');
    navigate('/', { replace: true });
    setTimeout(() => window.location.reload(), 100);
  };

  return (
    <div className="top-bar-right">
      {showLevel && (
        <div className="level-chip">
          <span className="level-chip-num">Lv {level}</span>
          <span className="level-chip-title">{getLevelTitle(level)}</span>
        </div>
      )}
      <div className="menu-wrap">
        <button
          onClick={() => setMenuOpen((o) => !o)}
          className="menu-trigger"
          title="Menu"
          aria-haspopup="true"
          aria-expanded={menuOpen}
        >
          <FaUser />
        </button>
        {menuOpen && (
          <>
            <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />
            <div className="menu-dropdown" role="menu">
              <div className="menu-id">
                <div className="menu-avatar"><FaUser /></div>
                <div>
                  <div className="menu-name">{user?.username || user?.name || 'Guest'}</div>
                  <div className="menu-sub">Level {level} · {getLevelTitle(level)}</div>
                </div>
              </div>
              <div className="menu-divider" />
              <button className="menu-item" onClick={() => go('/dashboard')}>
                <FaHome /><span>Home</span>
              </button>
              <button className="menu-item" onClick={() => go('/profile')}>
                <FaUser /><span>Profile</span>
              </button>
              {isAdmin && (
                <button className="menu-item" onClick={() => go('/admin')}>
                  <FaUserShield /><span>Admin Panel</span>
                </button>
              )}
              {isTeacherOrAdmin && (
                <button className="menu-item" onClick={() => go('/leagues')}>
                  <FaUsers /><span>League Management</span>
                </button>
              )}
              <button className="menu-item" onClick={() => go('/help')}>
                <FaLifeRing /><span>Help</span>
              </button>
              <div className="menu-divider" />
              <button
                className="menu-item menu-theme"
                onClick={toggleTheme}
                role="menuitemcheckbox"
                aria-checked={theme === 'dark'}
              >
                {theme === 'dark' ? <FaMoon /> : <FaSun />}
                <span>{theme === 'dark' ? 'Dark mode' : 'Light mode'}</span>
                <span className={`theme-switch ${theme === 'dark' ? 'on' : ''}`}>
                  <span className="theme-knob" />
                </span>
              </button>
              <div className="menu-divider" />
              <button className="menu-item menu-danger" onClick={handleLogout}>
                <FaSignOutAlt /><span>Log out</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfileMenu;
